<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div v-if="visible" class="whats-new-overlay" @click.self="close">
                <div class="whats-new-modal">
                    <!-- Header -->
                    <div class="modal-header">
                        <div class="header-badge">
                            <i class="fas fa-sparkles"></i>
                            Nouveautés
                        </div>
                        <div class="header-version">v2.4</div>
                        <button @click="close" class="close-btn">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <!-- Steps indicator -->
                    <div class="steps-indicator">
                        <button
                            v-for="(step, index) in steps"
                            :key="index"
                            @click="currentStep = index"
                            :class="['step-dot', { active: currentStep === index, completed: currentStep > index }]"
                        >
                            <span class="step-number">{{ index + 1 }}</span>
                        </button>
                        <div class="step-line">
                            <div class="step-line-progress" :style="{ width: (currentStep / (steps.length - 1)) * 100 + '%' }"></div>
                        </div>
                    </div>

                    <!-- Step content -->
                    <div class="step-content">
                        <Transition :name="slideDirection" mode="out-in">
                            <div :key="currentStep" class="step-body">
                                <!-- Step 1: Interface de vente -->
                                <div v-if="currentStep === 0" class="step-inner">
                                    <div class="step-illustration">
                                        <div class="illustration-icon sale-icon">
                                            <i class="fas fa-cash-register"></i>
                                        </div>
                                    </div>
                                    <h2 class="step-title">Interface de vente repensée</h2>
                                    <p class="step-description">
                                        L'expérience de vente a été entièrement redessinée pour plus de rapidité et de clarté.
                                    </p>
                                    <div class="features-list">
                                        <div class="feature-item">
                                            <div class="feature-icon green">
                                                <i class="fas fa-check"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Bouton "Valider la vente"</strong> visible en permanence dans le panier
                                            </div>
                                        </div>
                                        <div class="feature-item">
                                            <div class="feature-icon blue">
                                                <i class="fas fa-credit-card"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Modal de paiement compact</strong> avec les 5 méthodes en grille
                                            </div>
                                        </div>
                                        <div class="feature-item">
                                            <div class="feature-icon purple">
                                                <i class="fas fa-bolt"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Nouvelle vente instantanée</strong> sans dialogue de confirmation inutile
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Step 2: Design unifié -->
                                <div v-if="currentStep === 1" class="step-inner">
                                    <div class="step-illustration">
                                        <div class="illustration-icon design-icon">
                                            <i class="fas fa-paint-brush"></i>
                                        </div>
                                    </div>
                                    <h2 class="step-title">Design unifié et moderne</h2>
                                    <p class="step-description">
                                        Toutes les pages adoptent un style cohérent avec des couleurs harmonieuses et des composants épurés.
                                    </p>
                                    <div class="features-list">
                                        <div class="feature-item">
                                            <div class="feature-icon teal">
                                                <i class="fas fa-palette"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Pages d'authentification</strong> avec fond sombre et carte blanche lisible
                                            </div>
                                        </div>
                                        <div class="feature-item">
                                            <div class="feature-icon orange">
                                                <i class="fas fa-layer-group"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Modals redessinés</strong> avec animations fluides et taille optimisée
                                            </div>
                                        </div>
                                        <div class="feature-item">
                                            <div class="feature-icon pink">
                                                <i class="fas fa-mobile-alt"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Facture pro forma</strong> alignée avec le nouveau design system
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Step 3: Performance -->
                                <div v-if="currentStep === 2" class="step-inner">
                                    <div class="step-illustration">
                                        <div class="illustration-icon perf-icon">
                                            <i class="fas fa-rocket"></i>
                                        </div>
                                    </div>
                                    <h2 class="step-title">Performance et fiabilité</h2>
                                    <p class="step-description">
                                        Des optimisations sous le capot pour une application plus rapide et plus stable.
                                    </p>
                                    <div class="features-list">
                                        <div class="feature-item">
                                            <div class="feature-icon green">
                                                <i class="fas fa-broom"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Code nettoyé</strong> suppression du CSS dupliqué et du code de debug
                                            </div>
                                        </div>
                                        <div class="feature-item">
                                            <div class="feature-icon blue">
                                                <i class="fas fa-keyboard"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Raccourcis clavier</strong> Ctrl+Entrée pour valider, Echap pour fermer
                                            </div>
                                        </div>
                                        <div class="feature-item">
                                            <div class="feature-icon purple">
                                                <i class="fas fa-print"></i>
                                            </div>
                                            <div class="feature-text">
                                                <strong>Impression automatique</strong> du ticket thermique après chaque vente
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Transition>
                    </div>

                    <!-- Footer -->
                    <div class="modal-footer">
                        <button v-if="currentStep > 0" @click="prev" class="btn-secondary">
                            <i class="fas fa-arrow-left"></i>
                            Précédent
                        </button>
                        <div v-else></div>

                        <button v-if="currentStep < steps.length - 1" @click="next" class="btn-primary">
                            Suivant
                            <i class="fas fa-arrow-right"></i>
                        </button>
                        <button v-else @click="close" class="btn-primary btn-finish">
                            <i class="fas fa-check"></i>
                            C'est parti !
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
import { defineComponent, ref } from 'vue'

const WHATS_NEW_VERSION = 'v2.4'
const STORAGE_KEY = 'sellhub_whats_new_seen'

export default defineComponent({
    name: 'WhatsNewModal',

    setup() {
        const visible = ref(false)
        const currentStep = ref(0)
        const slideDirection = ref('slide-left')

        const steps = [
            { title: 'Interface de vente' },
            { title: 'Design unifié' },
            { title: 'Performance' }
        ]

        const checkAndShow = () => {
            const seen = localStorage.getItem(STORAGE_KEY)
            if (seen !== WHATS_NEW_VERSION) {
                visible.value = true
            }
        }

        const close = () => {
            visible.value = false
            localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION)
        }

        const next = () => {
            if (currentStep.value < steps.length - 1) {
                slideDirection.value = 'slide-left'
                currentStep.value++
            }
        }

        const prev = () => {
            if (currentStep.value > 0) {
                slideDirection.value = 'slide-right'
                currentStep.value--
            }
        }

        return {
            visible,
            currentStep,
            steps,
            slideDirection,
            checkAndShow,
            close,
            next,
            prev
        }
    }
})
</script>

<style scoped>
.whats-new-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
}

.whats-new-modal {
    background: white;
    border-radius: 20px;
    width: 100%;
    max-width: 60%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.35);
    animation: modal-enter 0.35s ease-out;
    overflow: hidden;
}

@keyframes modal-enter {
    from {
        opacity: 0;
        transform: scale(0.9) translateY(20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

/* Header */
.modal-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.header-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #0A5C6B;
    color: white;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
}

.header-version {
    font-size: 13px;
    color: #94a3b8;
    font-weight: 500;
}

.close-btn {
    margin-left: auto;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    background: white;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.close-btn:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
}

/* Steps indicator */
.steps-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
    padding: 24px 24px 0;
    position: relative;
}

.step-line {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, calc(-50% + 12px));
    width: 160px;
    height: 3px;
    background: #e2e8f0;
    border-radius: 2px;
    z-index: 0;
}

.step-line-progress {
    height: 100%;
    background: #0A5C6B;
    border-radius: 2px;
    transition: width 0.4s ease;
}

.step-dot {
    position: relative;
    z-index: 1;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2.5px solid #e2e8f0;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s;
}

.step-dot.active {
    border-color: #0A5C6B;
    background: #0A5C6B;
    box-shadow: 0 0 0 4px rgba(10, 92, 107, 0.15);
}

.step-dot.completed {
    border-color: #0A5C6B;
    background: #0A5C6B;
}

.step-number {
    font-size: 13px;
    font-weight: 700;
    color: #94a3b8;
    transition: color 0.3s;
}

.step-dot.active .step-number,
.step-dot.completed .step-number {
    color: white;
}

/* Step content */
.step-content {
    flex: 1;
    overflow-y: auto;
    padding: 28px 32px;
    min-height: 380px;
}

.step-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.step-illustration {
    margin-bottom: 24px;
}

.illustration-icon {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
}

.sale-icon {
    background: linear-gradient(135deg, #e0f7fa, #b2ebf2);
    color: #0A5C6B;
}

.design-icon {
    background: linear-gradient(135deg, #ede9fe, #ddd6fe);
    color: #7c3aed;
}

.perf-icon {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #d97706;
}

.step-title {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 8px;
    text-align: center;
}

.step-description {
    font-size: 14px;
    color: #64748b;
    text-align: center;
    margin: 0 0 28px;
    line-height: 1.6;
    max-width: 420px;
}

/* Features list */
.features-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
}

.feature-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 14px 16px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #f1f5f9;
    transition: all 0.2s;
}

.feature-item:hover {
    background: #f1f5f9;
    border-color: #e2e8f0;
}

.feature-icon {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
}

.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #ede9fe; color: #7c3aed; }
.feature-icon.teal { background: #ccfbf1; color: #0d9488; }
.feature-icon.orange { background: #ffedd5; color: #ea580c; }
.feature-icon.pink { background: #fce7f3; color: #db2777; }

.feature-text {
    font-size: 13px;
    color: #475569;
    line-height: 1.5;
}

.feature-text strong {
    color: #1e293b;
    display: block;
    margin-bottom: 2px;
}

/* Footer */
.modal-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-top: 1px solid #f1f5f9;
    background: #f8fafc;
}

.btn-secondary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
}

.btn-primary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 22px;
    background: #0A5C6B;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary:hover {
    background: #094854;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.btn-finish {
    padding: 10px 28px;
    background: linear-gradient(135deg, #0A5C6B, #0d8a9c);
}

.btn-finish:hover {
    background: linear-gradient(135deg, #094854, #0A5C6B);
}

/* Transitions */
.modal-fade-enter-active {
    transition: opacity 0.3s ease;
}
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition: all 0.3s ease;
}

.slide-left-enter-from {
    opacity: 0;
    transform: translateX(30px);
}
.slide-left-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}
.slide-right-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}
.slide-right-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* Responsive */
@media (max-width: 640px) {
    .whats-new-modal {
        max-width: 100%;
        border-radius: 16px;
    }

    .step-content {
        padding: 20px;
        min-height: 340px;
    }

    .step-title {
        font-size: 19px;
    }

    .illustration-icon {
        width: 64px;
        height: 64px;
        font-size: 26px;
    }
}
</style>
